import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { apiLogout, apiSession, apiSessionExtend, apiSessionReauth } from '~constants/api';
import { BACKGROUND_HEADER, MS_PER_SECOND } from '~constants/session';

import type { SessionTiming } from '~main/reducers/login';

export const typePrefix = 'session';

/** The API's SessionStatus. Every duration is relative seconds, so the browser's clock never decides expiry. */
export interface SessionStatusResponse {
  expires_in: number;
  absolute_expires_in: number;
  expires_at: string;
  idle_expires_at: string;
  absolute_expires_at: string;
  idle_timeout_seconds: number;
  absolute_timeout_seconds: number;
  warning_seconds: number;
  reauth_required: boolean;
  reauth_grace_seconds: number;
  reauth_expires_in: number;
  reauth_expires_at: string;
}

export interface SessionRejectedPayload {
  message: string;
  reason?: string;
  status?: number;
}

export const toSessionTiming = (status: SessionStatusResponse, now: number = Date.now()): SessionTiming => ({
  expiresAt: now + status.expires_in * MS_PER_SECOND,
  absoluteExpiresAt: now + status.absolute_expires_in * MS_PER_SECOND,
  reauthExpiresAt: now + status.reauth_expires_in * MS_PER_SECOND,
  warningSeconds: status.warning_seconds,
  idleTimeoutSeconds: status.idle_timeout_seconds,
  absoluteTimeoutSeconds: status.absolute_timeout_seconds,
  reauthGraceSeconds: status.reauth_grace_seconds,
});

const UNKNOWN_REJECTION =
  'Request was rejected on the server with an unknown reasoning. Please contact your system administrator';

// Keeps the API's `reason` and HTTP status, which the generic action helper strips.
const toRejection = (error: unknown): SessionRejectedPayload => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const { message, reason } = error.response.data ?? {};
      return { message: message ?? UNKNOWN_REJECTION, reason, status: error.response.status };
    }
    if (error.code === 'ERR_NETWORK') {
      return { message: 'Network error: No response received from API. Please contact your administrator' };
    }
  }
  return { message: error instanceof Error ? error.message : 'Unknown error occurred' };
};

type ThunkConfig = { rejectValue: SessionRejectedPayload };

/**
 * Asks the API whether the cookie session is alive. Doubles as the page-load check that replaces the old
 * localStorage flag, so a reload no longer races the PrivateRoute redirect.
 */
export const bootstrapSession = createAsyncThunk<SessionStatusResponse, void, ThunkConfig>(
  `${typePrefix}/bootstrap`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(apiSession);
      return data.data as SessionStatusResponse;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  },
);

/** Slides the idle window. Used by "Stay signed in" and the typing heartbeat. */
export const extendSession = createAsyncThunk<SessionStatusResponse, void, ThunkConfig>(
  `${typePrefix}/extend`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(apiSessionExtend, {});
      return data.data as SessionStatusResponse;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  },
);

/** A read that never slides the session: used to learn exact deadlines and to check for another tab's work. */
export const fetchSessionStatus = createAsyncThunk<SessionStatusResponse, void, ThunkConfig>(
  `${typePrefix}/status`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(apiSession, { headers: { [BACKGROUND_HEADER]: '1' } });
      return data.data as SessionStatusResponse;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  },
);

/**
 * Confirms the password in place. The API rotates the cookie and starts a fresh 12 hour window on the same
 * browser key. The caller AES-encrypts the password first, so the plain text never enters action history.
 * Reasons: `invalid`, `user-locked`, `company-disabled`, `password-required`.
 */
export const reauthSession = createAsyncThunk<SessionStatusResponse, { encryptedPassword: string }, ThunkConfig>(
  `${typePrefix}/reauth`,
  async ({ encryptedPassword }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(apiSessionReauth, {
        password: encryptedPassword,
      });
      return data.data as SessionStatusResponse;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  },
);

/** Revokes the session on the API. Never rejects: signing out must always finish client-side. */
export const logout = createAsyncThunk<void, void>(`${typePrefix}/logout`, async () => {
  try {
    await axios.post(apiLogout, {});
  } catch {
    // The caller clears client state regardless.
  }
});
